/* global BigInt */
// src/pages/BuyChips.js
import React, { useState, useRef, useEffect } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { PublicKey, Transaction } from '@solana/web3.js';
import {
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
  createTransferInstruction,
  TOKEN_PROGRAM_ID,
  getAccount
} from '@solana/spl-token';
import { useNavigate } from 'react-router-dom';
import { TransactionInstruction } from '@solana/web3.js';
import api from '../api'; // adjust path if needed
import BN from 'bn.js';

// Environment‑driven constants 
const USDT_MINT = process.env.REACT_APP_USDT_SOL_ADDRESS || 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB'; // Default Solana USDT
const PROJECT_WALLET = process.env.REACT_APP_PROJECT_WALLET_ADDRESS;
const DECIMALS = 6; // USDT on Solana has 6 decimals

// Pack definitions with prices in USD (0.001$ per chip)
const packs = [
  { id: 'pack_10k', label: '10K', chips: 10000, usdPrice: 10.00, popular: false, discount: 'BASIC PACK' },
  { id: 'pack_25k', label: '25K', chips: 25000, usdPrice: 24.50, popular: true, discount: '2% OFF' },
  { id: 'pack_50k', label: '50K', chips: 50000, usdPrice: 48.00, popular: false, discount: '4% OFF' },
  { id: 'pack_100k', label: '100K', chips: 100000, usdPrice: 95.00, popular: false, discount: '5% OFF' },
];

const faqItems = [
  {
    question: "How do I use my chips?",
    answer: "Your chips will be instantly added to your account balance after purchase. You can use them immediately to bet on our platform."
  },
  {
    question: "Is there a purchase limit?",
    answer: "There are no limits on how many chips you can purchase. Feel free to buy as many as you need for your betting sessions."
  },
  {
    question: "What payment methods are accepted?",
    answer: "We currently accept USDT on Solana for all purchases. Make sure you have USDT in your connected wallet before making a purchase."
  },
  {
    question: "How long does delivery take?",
    answer: "Chip delivery is instant! Once your transaction is confirmed on the blockchain, the chips will be immediately available in your account."
  },
  {
    question: "What's the price per chip?",
    answer: "Each chip costs $0.001 (one-tenth of a cent). We offer volume discounts on larger packs."
  }
];

// Local storage keys for transaction tracking
const LS_KEYS = {
  PROCESSING: 'processingChipPurchase',
  TX_SIG: 'chipPurchaseTxSignature',
  PACK_ID: 'chipPurchasePackId',
  START_TIME: 'chipPurchaseStartTime'
};

export default function BuyChips() {
  const { connection } = useConnection();
  const { setVisible } = useWalletModal();    
  const [loadingPack, setLoadingPack] = useState(null);
  const [txSig, setTxSig] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [showWalletOptions, setShowWalletOptions] = useState(false);
  const [recoveryInProgress, setRecoveryInProgress] = useState(false);
  const walletOptionsRef = useRef(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const {
    publicKey,
    sendTransaction,
    connected,
    disconnect,
    wallets,
    select,
    connect
  } = useWallet();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (walletOptionsRef.current && !walletOptionsRef.current.contains(event.target)) {
        setShowWalletOptions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [walletOptionsRef]);

  useEffect(() => {
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);

  // Check for pending transactions on page load
  useEffect(() => {
    // We run this immediately on component mount and don't wait for wallet connection
    checkPendingTransactions();
    
    // We also run it when wallet connects
    if (connected) {
      checkPendingTransactions();
    }
  }, [connected, connection]);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const copyAddressToClipboard = () => {
    if (publicKey) {
      navigator.clipboard.writeText(publicKey.toString());
      setSuccess("Address copied to clipboard!");
      setTimeout(() => setSuccess(""), 2000);
      setShowWalletOptions(false);
    }
  };

  const handleDisconnect = () => {
    disconnect();
    setShowWalletOptions(false);
  };

  const toggleWalletOptions = () => {
    setShowWalletOptions(!showWalletOptions);
  };

  // Check if token account exists
  const accountExists = async (address) => {
    try {
      await getAccount(connection, address);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Wait for transaction confirmation with timeout
  const waitForConfirmation = async (connection, signature, timeoutMs = 60_000) => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      try {
        const resp = await connection.getSignatureStatuses([signature]);
        const info = resp.value[0];
        if (info) {
          if (info.err) {
            throw new Error(`Transaction failed: ${JSON.stringify(info.err)}`);
          }
          if (
            info.confirmationStatus === 'confirmed' ||
            info.confirmationStatus === 'finalized'
          ) {
            return info;
          }
        }
        await new Promise((r) => setTimeout(r, 2000));
      } catch (err) {
        console.error("Error checking confirmation:", err);
        // Continue trying despite errors
      }
    }
    throw new Error(`Timed out after ${timeoutMs / 1000}s waiting for confirmation`);
  };

  // Save transaction progress to localStorage
  const saveTransactionProgress = (packId, txSig = null) => {
    // Always save these three first
    localStorage.setItem(LS_KEYS.PROCESSING, 'true');
    localStorage.setItem(LS_KEYS.PACK_ID, packId);
    localStorage.setItem(LS_KEYS.START_TIME, Date.now().toString());
    
    // Only save sig if we have one
    if (txSig) {
      localStorage.setItem(LS_KEYS.TX_SIG, txSig);
    }
  };

  // Clear transaction progress from localStorage
  const clearTransactionProgress = () => {
    localStorage.removeItem(LS_KEYS.PROCESSING);
    localStorage.removeItem(LS_KEYS.TX_SIG);
    localStorage.removeItem(LS_KEYS.PACK_ID);
    localStorage.removeItem(LS_KEYS.START_TIME);
  };

  // Check for pending transactions when component loads or wallet connects
  const checkPendingTransactions = async () => {
    // Stop if already running recovery process
    if (recoveryInProgress) return;
    
    const isProcessing = localStorage.getItem(LS_KEYS.PROCESSING) === 'true';
    const txSig = localStorage.getItem(LS_KEYS.TX_SIG);
    const packId = localStorage.getItem(LS_KEYS.PACK_ID);
    const startTimeStr = localStorage.getItem(LS_KEYS.START_TIME);
    const startTime = startTimeStr ? parseInt(startTimeStr) : 0;
    
    if (!isProcessing) return;
    
    setRecoveryInProgress(true);
    setError('Checking status of your previous purchase...');
    
    try {
      // If we have a transaction signature, check its status
      if (txSig) {
        setTxSig(txSig);
        
        // Pack information to display in messages
        const pack = packs.find(p => p.id === packId);
        if (!pack) {
          throw new Error(`Unknown pack ID: ${packId}`);
        }
        
        setLoadingPack(packId);
        
        try {
          // Check transaction status on blockchain
          const status = await connection.getSignatureStatus(txSig);
          console.log('Previous transaction status:', status);
          
          if (!status?.value) {
            throw new Error('Transaction not found on blockchain');
          }
          
          if (status.value.err) {
            setError(`Previous transaction failed: ${JSON.stringify(status.value.err)}`);
            clearTransactionProgress();
            setLoadingPack(null);
            setRecoveryInProgress(false);
            return;
          }
          
          // Transaction is on chain and didn't fail
          if (
            status.value.confirmationStatus === 'confirmed' ||
            status.value.confirmationStatus === 'finalized'
          ) {
            // Transaction confirmed, now notify backend
            try {
              const token = localStorage.getItem('token');
              if (!token) {
                setError('Please log in to complete your purchase');
                setLoadingPack(null);
                setRecoveryInProgress(false);
                return;
              }
              
              const response = await api.post('/buy-chips', { 
                signature: txSig, 
                packId: packId 
              });
              
              // Successfully credited the chips!
              setSuccess(`You've successfully purchased ${pack.chips.toLocaleString()} chips!`);
              clearTransactionProgress();
            } catch (apiErr) {
              // Check if the error is because transaction was already processed (which is fine)
              if (apiErr.response?.data?.alreadyProcessed) {
                setSuccess(`You've already been credited ${pack.chips.toLocaleString()} chips for this transaction!`);
                clearTransactionProgress();
              } else {
                setError(`Error processing purchase: ${apiErr.response?.data?.error || apiErr.message}`);
                // We don't clear transaction data here so user can retry
              }
            }
          } else {
            // Transaction is still pending
            setError('Your previous transaction is still being confirmed. Please wait...');
            // Don't clear transaction data as it's still processing
          }
        } catch (statusErr) {
          console.error('Error checking transaction status:', statusErr);
          
          // If it's been more than 10 minutes, we'll give up and let user retry
          const now = Date.now();
          const timeElapsed = now - startTime;
          
          if (timeElapsed > 10 * 60 * 1000) {
            setError(`Unable to verify transaction status. Please contact support with this ID: ${txSig}`);
            // We don't clear transaction data here so they have the tx ID
          } else {
            setError('Still checking transaction status...');
            // Don't clear transaction data
          }
        }
      } else if (packId) {
        // We have a pending pack but no signature yet
        // (user refreshed before signature was saved)
        const now = Date.now();
        const timeElapsed = now - startTime;
        
        if (timeElapsed > 5 * 60 * 1000) {
          // It's been more than 5 minutes, assume transaction never completed
          setError('Previous purchase attempt timed out. You can try again.');
          clearTransactionProgress();
        } else {
          setError('You have a pending purchase. Please check your wallet for approval requests.');
          // Don't clear transaction data yet
        }
      }
    } catch (err) {
      console.error('Error in recovery process:', err);
      setError(`Recovery process error: ${err.message}`);
      // Don't clear transaction data in case of error
    } finally {
      setLoadingPack(null);
      setRecoveryInProgress(false);
    }
  };

  const buyPack = async (pack) => {
    if (!connected || !publicKey) {
      return alert('Please connect your wallet.');
    }

    // Reset UI state
    setError('');
    setTxSig('');
    setSuccess('');
    setLoadingPack(pack.id);
    
    // Save the initial transaction state before any blockchain interaction
    saveTransactionProgress(pack.id);
  
    try {
      console.log('Starting purchase process for pack:', pack.id);
      
      const mint = new PublicKey(USDT_MINT);
      const projectPubkey = new PublicKey(PROJECT_WALLET);
      const owner = publicKey;
      
      console.log('User wallet:', owner.toString());
      console.log('Is mobile device:', isMobile);
      
      // Check SOL balance first
      const solBalance = await connection.getBalance(owner);
      console.log('SOL balance:', solBalance / 1e9, 'SOL');
      
      if (solBalance < 5000000) { // 0.005 SOL minimum for fees
        throw new Error('Insufficient SOL balance for transaction fees');
      }
      
      // Get user's token account
      const userAtaAddr = await getAssociatedTokenAddress(mint, owner);
      console.log('User token account:', userAtaAddr.toString());
      
      // Check if user has the USDT tokens before proceeding
      let userAccountExists = false;
      let userTokenBalance = BigInt(0);
      
      try {
        const userAccount = await getAccount(connection, userAtaAddr);
        userAccountExists = true;
        userTokenBalance = userAccount.amount;
        console.log('User USDT balance:', userTokenBalance.toString());
        
        // Check if user has enough tokens
        const requiredAmount = BigInt(Math.round(pack.usdPrice * Math.pow(10, DECIMALS)));
        if (userTokenBalance < requiredAmount) {
          throw new Error(`Insufficient USDT. You need $${pack.usdPrice} USDT but have ${Number(userTokenBalance) / Math.pow(10, DECIMALS)}.`);
        }
      } catch (e) {
        if (e.message.includes('Insufficient')) {
          throw e; // Re-throw balance errors
        }
        console.log('User token account does not exist or error:', e.message);
        userAccountExists = false;
      }
      
      // Mobile-specific transaction handling
      if (isMobile) {
        console.log('Using mobile-optimized transaction flow');
        // Increase timeouts for mobile
        const mobileTimeout = 120000; // 2 minutes
        
        // Get a fresh blockhash with longer validity
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');
        
        // Create a SINGLE transaction for mobile to avoid multiple app switches
        const mobileTx = new Transaction();
        mobileTx.feePayer = owner;
        mobileTx.recentBlockhash = blockhash;

        // Add memo instruction to identify the transaction
        mobileTx.add(
          new TransactionInstruction({
            keys: [],
            programId: new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr"),
            data: new TextEncoder().encode("Prediction Arena - Purchasing chips pack: " + pack.id),
          })
        );
        
        // Create token account if needed
        if (!userAccountExists) {
          console.log('Adding instruction to create user token account');
          mobileTx.add(
            createAssociatedTokenAccountInstruction(
              owner,         // payer
              userAtaAddr,   // ATA address
              owner,         // ATA owner
              mint
            )
          );
        }
        
        // Check project token account
        const projectAtaAddr = await getAssociatedTokenAddress(mint, projectPubkey);
        console.log('Project token account:', projectAtaAddr.toString());
        
        let projectAccountExists = false;
        try {
          await getAccount(connection, projectAtaAddr);
          projectAccountExists = true;
          console.log('Project token account exists');
        } catch (e) {
          console.log('Project token account does not exist');
          projectAccountExists = false;
        }
        
        // Create project account if needed
        if (!projectAccountExists) {
          console.log('Adding instruction to create project token account');
          mobileTx.add(
            createAssociatedTokenAccountInstruction(
              owner,           // payer
              projectAtaAddr,  // ATA address
              projectPubkey,   // ATA owner
              mint
            )
          );
        }

        console.log('Pack USD price:', pack.usdPrice);
        console.log('DECIMALS value:', DECIMALS);
        
        // Calculate transfer amount
        const transferAmount = BigInt(Math.round(pack.usdPrice * Math.pow(10, DECIMALS)));
        console.log('Transfer amount (raw):', transferAmount.toString());
        
        // Add transfer instruction
        console.log('Adding transfer instruction for mobile');
        mobileTx.add(
          createTransferInstruction(
            userAtaAddr,      // from
            projectAtaAddr,   // to
            owner,            // owner
            transferAmount,   // amount
            [],               // multisig signers (empty)
            TOKEN_PROGRAM_ID
          )
        );
        
        console.log('Mobile transaction prepared, sending...');
        
        // For mobile, add a longer timeout
        const mobileConfig = {
          skipPreflight: false,
          preflightCommitment: 'processed',
          maxRetries: 5,
        };
        
        try {
          // Send mobile transaction
          const sig = await sendTransaction(mobileTx, connection, mobileConfig);
          console.log('Mobile transaction sent, signature:', sig);
          setTxSig(sig);
          
          // Update localStorage with signature
          saveTransactionProgress(pack.id, sig);
          
          // Wait for confirmation with longer timeout
          console.log('Waiting for mobile transaction confirmation...');
          await waitForConfirmation(connection, sig, mobileTimeout);
          
          // Notify backend
          console.log('Notifying backend for mobile transaction...');
          const response = await api.post('/buy-chips', { signature: sig, packId: pack.id });
          setSuccess(`You've successfully purchased ${pack.chips.toLocaleString()} chips!`);
          
          // Clear localStorage now that we're done
          clearTransactionProgress();
        } catch (err) {
          console.error('Mobile transaction error:', err);
          if (err.message?.includes('User rejected') || err.message?.includes('cancelled')) {
            setError('Transaction was rejected in the wallet app. Please try again.');
            clearTransactionProgress(); // Clear since user explicitly rejected
          } else {
            setError(`Mobile transaction error: ${err.message || 'Unknown error'}`);
            // Don't clear localStorage for other errors so we can recover
          }
        }
      } else {
        // Desktop flow
        console.log('Using desktop transaction flow');
        
        // Get recent blockhash
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');
        
        // TRANSACTION 1: Create accounts if needed
        if (!userAccountExists) {
          console.log('Creating user token account first...');
          const createAccountTx = new Transaction();
          createAccountTx.feePayer = owner;
          createAccountTx.recentBlockhash = blockhash;
          
          createAccountTx.add(
            createAssociatedTokenAccountInstruction(
              owner,         // payer
              userAtaAddr,   // ATA address
              owner,         // ATA owner
              mint
            )
          );
          
          const createSig = await sendTransaction(createAccountTx, connection);
          console.log('Account creation transaction sent:', createSig);
          
          // Wait for confirmation
          await connection.confirmTransaction({
            signature: createSig,
            blockhash: blockhash,
            lastValidBlockHeight: lastValidBlockHeight
          });
          
          console.log('User token account created successfully');
        }
        
        // Get a fresh blockhash for the transfer transaction
        const freshBlockhashData = await connection.getLatestBlockhash('finalized');
        
        // TRANSACTION 2: Transfer tokens
        const transferTx = new Transaction();
        transferTx.feePayer = owner;
        transferTx.recentBlockhash = freshBlockhashData.blockhash;
        
        // Add memo instruction
        transferTx.add(
          new TransactionInstruction({
            keys: [],
            programId: new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr"),
            data: new TextEncoder().encode("Prediction Arena - Purchasing chips pack: " + pack.id),
          })
        );
        
        // Check project token account
        const projectAtaAddr = await getAssociatedTokenAddress(mint, projectPubkey);
        console.log('Project token account:', projectAtaAddr.toString());
        
        let projectAccountExists = false;
        try {
          await getAccount(connection, projectAtaAddr);
          projectAccountExists = true;
          console.log('Project token account exists');
        } catch (e) {
          console.log('Project token account does not exist');
          projectAccountExists = false;
        }
        
        // Create project account if needed
        if (!projectAccountExists) {
          console.log('Adding instruction to create project token account');
          transferTx.add(
            createAssociatedTokenAccountInstruction(
              owner,           // payer
              projectAtaAddr,  // ATA address
              projectPubkey,   // ATA owner
              mint
            )
          );
        }

        console.log('Pack USD price:', pack.usdPrice);
        console.log('DECIMALS value:', DECIMALS);
        
        // Calculate transfer amount - ensure it's a valid integer
        const transferAmount = BigInt(Math.round(pack.usdPrice * Math.pow(10, DECIMALS)));
        console.log('Transfer amount (raw):', transferAmount.toString());
        
        // Add transfer instruction
        console.log('Adding transfer instruction');
        transferTx.add(
          createTransferInstruction(
            userAtaAddr,      // from
            projectAtaAddr,   // to
            owner,            // owner
            transferAmount,   // amount
            [],               // multisig signers (empty)
            TOKEN_PROGRAM_ID
          )
        );
        
        console.log('Transfer transaction prepared, sending...');
        
        try {
          // Send transfer transaction
          const sig = await sendTransaction(transferTx, connection);
          console.log('Transfer transaction sent, signature:', sig);
          
          setTxSig(sig);
          
          // Update localStorage with signature
          saveTransactionProgress(pack.id, sig);
          
          // Wait for confirmation
          await waitForConfirmation(connection, sig, 60_000);
          
          // Notify backend
          console.log('Notifying backend...');
          const response = await api.post('/buy-chips', { signature: sig, packId: pack.id });
          setSuccess(`You've successfully purchased ${pack.chips.toLocaleString()} chips!`);
          
          // Clear localStorage after successful purchase
          clearTransactionProgress();
        } catch (err) {
          console.error('Transaction Error:', err);
          
          if (err.message?.includes('User rejected')) {
            setError('Transaction was rejected in your wallet. Please try again.');
            clearTransactionProgress(); // Clear since user explicitly rejected
          } else if (err.message?.includes('timeout') || err.message?.includes('Timed out')) {
            setError('Connection to wallet timed out. Please check your wallet app is open and try again.');
            // Don't clear localStorage for timeouts so we can recover
          } else if (err.message?.includes('insufficient funds')) {
            setError('Insufficient funds in your wallet for this transaction.');
            clearTransactionProgress(); // Clear as transaction won't succeed
          } else {
            setError(err.response?.data?.error || err.message || 'An unexpected error occurred.');
            // Don't clear localStorage for other errors so we can recover
          }
        }
      }
      
    } catch (err) {
      console.error('Pre-transaction Error:', err);
      
      // Enhanced error handling
      if (err.message?.includes('Insufficient USDT')) {
        setError(err.message);
      } else if (err.message?.includes('Insufficient SOL balance')) {
        setError('Insufficient SOL balance for transaction fees. You need at least 0.005 SOL.');
      } else {
        setError(err.response?.data?.error || err.message || 'An unexpected error occurred.');
      }
      
      // Clear transaction state since we didn't even reach the blockchain
      clearTransactionProgress();
    } finally {
      setLoadingPack(null);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-900 to-black -mt-16 pt-4 pb-12 px-4 text-white font-mono">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 text-center mb-8">
            Purchase Chips
          </h1>
          <p className="text-gray-300 text-lg max-w-xl mx-auto">
            Top up your balance and get ready to play!
          </p>
        </div>
        
        {/* Custom Wallet Connection Section */}
        <div className="mb-10">
          {!connected ? (
            <div className="relative rounded-xl border border-gray-700 bg-gray-900/50 backdrop-blur-sm p-6 max-w-md mx-auto hover:shadow-xl hover:shadow-pink-500/10 transition-all duration-300">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-500 to-pink-500 text-white text-xs font-bold py-1 px-3 rounded-full">
                CONNECT FIRST
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-gray-800/60 flex items-center justify-center mb-4 border border-gray-700">
                  <svg className="w-10 h-10 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-bold mb-2">Wallet Required</h3>
                <p className="text-gray-300 mb-4 text-center text-sm">Connect your wallet to unlock chip purchases</p>
                
                <div className="bg-gray-800/60 rounded-lg p-3 mb-4 w-full">
                  <ul className="space-y-1 text-sm">
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Secure Connection</span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Fast Transactions</span>
                    </li>
                  </ul>
                </div>
                
                <button
                  onClick={async () => {
                    // 1) ensure user is logged in
                    const token = localStorage.getItem('token');
                    if (!token) return navigate('/login');

                    // 2) on mobile, deep-link via Mobile Wallet Adapter
                    if (isMobile) {
                      const mobileAdapter = wallets.find(w => w.name === 'Mobile Wallet Adapter');
                      if (mobileAdapter) {
                        select(mobileAdapter.name);
                        await connect();
                      } else {
                        setVisible(true); // fallback
                      }
                    } else {
                      // desktop: show normal modal
                      setVisible(true);
                    }
                  }}
                  className="w-full px-4 py-3 rounded-lg font-bold text-sm bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 transition-all duration-300 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  Connect Wallet
                </button>
              </div>
            </div>
          ) : (
            <div className="relative rounded-xl border border-gray-700 bg-gray-900/50 backdrop-blur-sm p-4 max-w-md mx-auto hover:shadow-xl hover:shadow-pink-500/10 transition-all duration-300">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-green-500 to-blue-500 text-white text-xs font-bold py-1 px-3 rounded-full">
                READY TO PLAY
              </div>
              
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-between w-full mb-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-gray-800/60 flex items-center justify-center mr-3 border border-green-500/50">
                      <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="text-base font-bold">Wallet Connected</h3>
                      <p className="text-gray-300 text-xs truncate max-w-xs">
                        {publicKey.toString().slice(0, 6)}...{publicKey.toString().slice(-4)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="relative" ref={walletOptionsRef}>
                    <button 
                      onClick={toggleWalletOptions}
                      className="w-9 h-9 rounded-lg bg-gray-800/60 hover:bg-gray-700/60 flex items-center justify-center transition-colors duration-200 border border-gray-700"
                    >
                      <svg className={`w-5 h-5 text-gray-400 transform transition-transform duration-200 ${showWalletOptions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {/* Custom Dropdown */}
                    {showWalletOptions && (
                      <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg bg-gray-800 border border-gray-700 overflow-hidden z-10">
                        <div className="py-1">
                          <button
                            onClick={copyAddressToClipboard}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                            </svg>
                            Copy Address
                          </button>
                          <button
                            onClick={() => {
                              setVisible(true);
                              setShowWalletOptions(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                            Change Wallet
                          </button>
                          <button
                            onClick={handleDisconnect}
                            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 hover:text-red-300 flex items-center"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Disconnect
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center justify-between w-full bg-gray-800/40 rounded-lg px-3 py-2 mb-3">
                  <span className="text-sm text-gray-300">Status:</span>
                  <span className="text-sm text-green-400 flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                    Ready to Purchase
                  </span>
                </div>
                
                <button
                  onClick={() => window.scrollTo({top: document.querySelector('.grid').offsetTop - 20, behavior: 'smooth'})}
                  className="w-full px-3 py-2.5 rounded-lg font-bold text-sm bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 transition-all duration-300 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  View Chip Packs
                </button>
              </div>
            </div>
          )}
          {isMobile && !connected && (
            <div className="mt-3 text-xs text-gray-400 px-3 py-2 bg-gray-800/30 rounded-lg">
              <p className="text-center">
                <svg className="w-4 h-4 inline-block mr-1 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                For mobile wallets, select "Mobile Adapter" or "WalletConnect" from the options after clicking Connect.
              </p>
            </div>
          )}
        </div>

        {/* Packages Grid - Only visible when wallet is connected */}
        {connected ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8">
            {packs.map((pack) => (
              <div
                key={pack.id}
                className={`relative rounded-xl border ${pack.popular ? 'border-pink-500' : 'border-gray-700'} bg-gray-900/50 backdrop-blur-sm p-3 sm:p-6 hover:shadow-xl hover:shadow-pink-500/10 transition-all transform hover:-translate-y-1 duration-300`}
              >
                {pack.popular && (
                  <div className="absolute -top-2 sm:-top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-500 to-pink-500 text-white text-xs font-bold py-0.5 sm:py-1 px-2 sm:px-3 rounded-full">
                    POPULAR
                  </div>
                )}
                
                <div className="text-center mb-2 sm:mb-3">
                  <h2 className="text-lg sm:text-2xl font-bold">{pack.label}</h2>
                  {pack.discount && (
                    <span className="inline-block bg-pink-500/20 text-pink-300 text-xs px-2 py-0.5 rounded">
                      {pack.discount}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center justify-center mb-2 sm:mb-3">
                  <span className="text-xl sm:text-3xl font-bold">${pack.usdPrice.toFixed(2)}</span>
                  <span className="ml-1 text-gray-400 text-xs sm:text-sm">USDT</span>
                </div>
                
                <div className="hidden sm:block bg-gray-800/60 rounded-lg p-3 mb-4">
                  <ul className="space-y-1 text-sm">
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{pack.chips.toLocaleString()} Chips</span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Instant Delivery</span>
                    </li>
                  </ul>
                </div>
                
                <button
                  onClick={() => buyPack(pack)}
                  // onClick={() => {}}
                  disabled={loadingPack === pack.id || recoveryInProgress}
                  className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg font-bold text-xs sm:text-sm transition-all duration-300 ${
                    pack.popular
                      ? 'bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600'
                      : 'bg-gradient-to-r from-violet-500/70 to-pink-500/70 hover:from-violet-500 hover:to-pink-500'
                  } disabled:opacity-50 flex items-center justify-center`}
                >
                  {loadingPack === pack.id ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-1 h-3 w-3 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="sm:block hidden">Processing...</span>
                      <span className="block sm:hidden">Wait...</span>
                    </>
                  ) : recoveryInProgress ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-1 h-3 w-3 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="sm:block hidden">Checking...</span>
                      <span className="block sm:hidden">Wait...</span>
                    </>
                  ) : (
                    <>
                      <span className="sm:block hidden">Purchase Now</span>
                      <span className="block sm:hidden">Buy</span>
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {/* Notifications */}
        {error && (
          <div className="mt-6 bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg text-center">
            <svg className="w-5 h-5 text-red-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
        
        {success && (
          <div className="mt-6 bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-3 rounded-lg text-center">
            <svg className="w-5 h-5 text-green-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {success}
          </div>
        )}
        
        {txSig && (
          <div className="mt-4 bg-gray-800/50 border border-gray-700 p-3 rounded-lg text-center">
            <p className="text-gray-400 text-sm">Transaction Signature:</p>
            <p className="text-green-400 font-mono text-xs mt-1 break-all">{txSig}</p>
          </div>
        )}

        {/* How it Works Section */}
        <div className="mt-12 mb-8">
          <h2 className="text-2xl font-bold mb-6 text-center">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-4 text-center hover:bg-gray-800/60 transition-colors">
              <div className="w-12 h-12 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2">1. Connect Wallet</h3>
              <p className="text-gray-300 text-sm">
                Connect your Solana wallet that contains USDT tokens to our platform
              </p>
            </div>
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-4 text-center hover:bg-gray-800/60 transition-colors">
              <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2">2. Choose Package</h3>
              <p className="text-gray-300 text-sm">
                Select a chip package and approve the transaction in your wallet
              </p>
            </div>
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-4 text-center hover:bg-gray-800/60 transition-colors">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2">3. Receive Chips</h3>
              <p className="text-gray-300 text-sm">
                Once verified, your chips are instantly credited to your account for immediate use
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section with Collapsible Items */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Frequently Asked Questions</h2>
          <div className="space-y-3 max-w-3xl mx-auto">
            {faqItems.map((item, index) => (
              <div 
                key={index} 
                className="border border-gray-700 rounded-lg overflow-hidden"
              >
                <button 
                  className="w-full p-3 sm:p-4 flex justify-between items-center bg-gray-800/40 hover:bg-gray-800/60 text-left transition-colors duration-200"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="font-medium text-sm sm:text-base">{item.question}</span>
                  <svg 
                    className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transform transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out bg-gray-800/20 ${
                    openFaq === index ? 'max-h-40 p-3 sm:p-4' : 'max-h-0'
                  }`}
                >
                  <p className="text-gray-300 text-sm sm:text-base">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
